// =====================================================
// 面包屑导航组件
// 参考 Apple 产品层级设计
// =====================================================

import React from 'react';
import { ChevronRight, Home } from 'lucide-react';
import type { BreadcrumbItem } from '../../types/hierarchy';

interface BreadcrumbProps {
  items: BreadcrumbItem[];
  onNavigate: (level: number, id?: string) => void;
}

export function Breadcrumb({ items, onNavigate }: BreadcrumbProps) {
  return (
    <nav className="flex items-center gap-1 text-sm" aria-label="面包屑导航">
      {/* 首页图标 */}
      <button
        onClick={() => onNavigate(1)}
        className="p-1 hover:bg-gray-100 rounded transition-colors"
        title="返回首页"
      >
        <Home className="w-4 h-4 text-gray-500" />
      </button>

      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        const isFirst = index === 0;

        // 跳过第一个"全部产品"项，因为已经用 Home 图标代替
        if (isFirst && item.name === '全部产品') {
          return null;
        }

        return (
          <React.Fragment key={`${item.level}-${item.id || item.name}`}>
            <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
            
            {isLast ? (
              // 当前层级（不可点击）
              <span className="text-gray-900 font-medium truncate max-w-[200px]">
                {item.name}
              </span>
            ) : (
              // 可点击的上级层级
              <button
                onClick={() => onNavigate(item.level, item.id)}
                className="text-gray-500 hover:text-blue-600 transition-colors truncate max-w-[150px]"
              >
                {item.name}
              </button>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
}

// =====================================================
// 带返回按钮的面包屑
// =====================================================

interface BreadcrumbWithBackProps extends BreadcrumbProps {
  onBack: () => void;
  canGoBack: boolean;
}

export function BreadcrumbWithBack({ 
  items, 
  onNavigate, 
  onBack, 
  canGoBack 
}: BreadcrumbWithBackProps) {
  return (
    <div className="flex items-center gap-3">
      {/* 返回按钮 */}
      {canGoBack && (
        <button
          onClick={onBack}
          className="flex items-center gap-1 px-2 py-1 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ChevronRight className="w-4 h-4 rotate-180" />
          返回
        </button>
      )}
      
      {/* 分隔线 */}
      {canGoBack && (
        <div className="w-px h-5 bg-gray-200" />
      )}
      
      {/* 面包屑 */}
      <Breadcrumb items={items} onNavigate={onNavigate} />
    </div>
  );
}

export default Breadcrumb;
